import { apiRequest, apiRequestBlob } from "./api";

// Stripe public config for the Payment Details form (publishable key + fee
// amount in cents). Used to init Stripe.js and render the Payment Element.
// GET /returns/{returnId}/service-fee/config -> { publishableKey, amount, currency, vehicleCount,
// serviceFeeAmount, processingFeeAmount, feeAmount, feeRequired } - amount is the TOTAL in cents
// (serviceFeeAmount + processingFeeAmount); the latter two are dollar strings for display.
export async function getServiceFeeConfig(returnId) {
  return apiRequest(`/returns/${returnId}/service-fee/config`);
}

// Create a Stripe PaymentIntent for the one-time e-filing service fee. Sends
// only billing details (NO card data - card data is collected by the Stripe
// Payment Element and sent browser-to-Stripe directly). The charge amount is
// always server-computed from the return, never from this request.
// POST /returns/{returnId}/service-fee/intent -> { clientSecret, publishableKey, paymentId }
export async function createServiceFeeIntent(returnId, form) {
  return apiRequest(`/returns/${returnId}/service-fee/intent`, {
    method: "POST",
    body: {
      firstName: form.firstName,
      lastName: form.lastName,
      postalCode: form.zip,
    },
  });
}

// Read the current service fee payment status for a return (used to confirm
// the webhook/reconcile has finalized the charge after confirmPayment, and to
// gate the form if it's already been paid).
// GET /returns/{returnId}/service-fee -> { status, ... }
export async function getServiceFeeStatus(returnId) {
  return apiRequest(`/returns/${returnId}/service-fee`);
}

// Whether extra vehicles added since a rejected return was originally filed/
// paid require an additional service fee before it can be resubmitted, and
// the exact amount if so - priced as a service_fee_rate band delta, never a
// fresh fee for the whole new vehicle count. required:false with
// extraVehicleCount > 0 is a real, expected case (the addition didn't cross
// a pricing band boundary, so it's still free).
// GET /returns/{returnId}/service-fee/topup-config -> same shape as getServiceFeeConfig,
// plus { required, extraVehicleCount }.
export async function getServiceFeeTopUpConfig(returnId) {
  return apiRequest(`/returns/${returnId}/service-fee/topup-config`);
}

// Create a Stripe PaymentIntent for the currently-owed top-up amount. Same
// billing-details-only pattern as createServiceFeeIntent - no card data, and
// the amount is always server-computed (re-validated against the live
// vehicle count, never trusted from a prior getServiceFeeTopUpConfig read).
// POST /returns/{returnId}/service-fee/topup-intent -> { clientSecret, publishableKey, paymentId }
export async function createServiceFeeTopUpIntent(returnId, form) {
  return apiRequest(`/returns/${returnId}/service-fee/topup-intent`, {
    method: "POST",
    body: {
      firstName: form.firstName,
      lastName: form.lastName,
      postalCode: form.zip,
    },
  });
}

// Status of the most recent top-up payment for a return - same poll-after-
// confirmPayment pattern as getServiceFeeStatus.
// GET /returns/{returnId}/service-fee/topup -> { status, ... }
export async function getServiceFeeTopUpStatus(returnId) {
  return apiRequest(`/returns/${returnId}/service-fee/topup`);
}

// Dashboard > Payments table: every service-fee payment across every return
// the caller owns, newest first. Dollar amounts (NOT cents, unlike
// getServiceFeeStatus above) - use utils/format.js's money() directly.
// GET /service-fee/payments -> ServiceFeeHistoryResponse[]
export async function getServiceFeePaymentHistory() {
  return apiRequest(`/service-fee/payments`);
}

// Receipt details for a single payment (confirmation #, business, etc.) -
// backs the receipt modal. `source` distinguishes which table `paymentId` means
// (a getServiceFeePaymentHistory() row's own `source` field - "SERVICE_FEE"
// (default, a real Stripe charge) or "SUBSCRIPTION_CREDIT" (a return paid for
// with a package credit - returns a credit-redemption receipt instead)).
// GET /service-fee/payments/{id}/receipt[?source=] -> ServiceFeeReceiptResponse
export async function getServiceFeeReceipt(paymentId, source) {
  const qs = source ? `?source=${encodeURIComponent(source)}` : "";
  return apiRequest(`/service-fee/payments/${paymentId}/receipt${qs}`);
}

// Downloads the receipt as a PDF. -> { blob, filename }
export async function downloadServiceFeeReceiptPdf(paymentId, source) {
  const qs = source ? `?source=${encodeURIComponent(source)}` : "";
  return apiRequestBlob(`/service-fee/payments/${paymentId}/receipt/pdf${qs}`, {
    fallbackFilename: `EasyFile2290-Receipt-${paymentId}.pdf`,
  });
}

// Emails the receipt PDF to the given address ("Resend" in the modal).
export async function emailServiceFeeReceipt(paymentId, source, email) {
  const qs = source ? `?source=${encodeURIComponent(source)}` : "";
  return apiRequest(`/service-fee/payments/${paymentId}/receipt/send${qs}`, {
    method: "POST",
    body: { email },
  });
}
